import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  filter,
  map,
  of,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import * as ReferentielActions from './referentiel.actions';
import * as ReferentielSelectors from './referentiel.selectors';
import { ReferentielService } from '../../core/services/referentiel.service';
import { Language, ServiceAvailable } from '../../core/interfaces/doctor.interface';

@Injectable()
export class ReferentielEffects {
  private actions$ = inject(Actions);
  private referentielService = inject(ReferentielService);
  private store = inject(Store);

  loadLanguages$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReferentielActions.loadLanguages),
      withLatestFrom(this.store.select(ReferentielSelectors.selectLanguages)),
      filter(([_, languages]) => !languages || languages.length === 0),
      switchMap(() =>
        this.referentielService.getLanguages().pipe(
          map((response) => {
            // Map the API response to Language interface
            const languages: Language[] = response.map((lang) => ({
              id: lang.id,
              label: lang.label,
            }));
            return ReferentielActions.loadLanguagesSuccess({ languages });
          }),
          catchError((error) =>
            of(
              ReferentielActions.loadLanguagesFailure({
                error: error.message || 'Erreur lors du chargement des langues',
              })
            )
          )
        )
      )
    );
  });

  loadServices$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReferentielActions.loadServices),
      withLatestFrom(this.store.select(ReferentielSelectors.selectServices)),
      filter(([_, services]) => !services || services.length === 0),
      switchMap(() =>
        this.referentielService.getServices().pipe(
          map((response) => {
            // Map the API response to Language interface
            const services: ServiceAvailable[] = response.map((service) => ({
              id: service.id,
              name: service.name,
            }));
            return ReferentielActions.loadServicesSuccess({ services });
          }),
          catchError((error) =>
            of(
              ReferentielActions.loadServicesFailure({
                error:
                  error.message || 'Erreur lors du chargement des services',
              })
            )
          )
        )
      )
    );
  });

  loadInsurers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReferentielActions.loadInsurers),
      switchMap(({ countryCode }) =>
        this.referentielService.getInsurers(countryCode).pipe(
          map((insurers) => ReferentielActions.loadInsurersSuccess({ insurers })),
          catchError((error) =>
            of(ReferentielActions.loadInsurersFailure({ error: error.message }))
          )
        )
      )
    )
  );

  loadCountries$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReferentielActions.loadCountries),
      withLatestFrom(this.store.select(ReferentielSelectors.selectCountries)),
      filter(([_, countries]) => !countries || countries.length === 0),
      switchMap(() =>
        this.referentielService.getCountries().pipe(
          map((countries) =>
            ReferentielActions.loadCountriesSuccess({ countries })
          ),
          catchError((error) =>
            of(
              ReferentielActions.loadCountriesFailure({
                error: error.message || 'Erreur lors du chargement des pays',
              })
            )
          )
        )
      )
    );
  });
}
